import { useEffect, useState } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

export default function Questionnaire() {
  const [feedback, setFeedback] = useState('');
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedback.trim()) return;

    setSubmitState('submitting');
    setErrorMessage('');

    try {
      const response = await fetch(
        'https://recover.redissprod.com.au/userapi/api/QaSession/SubmitQuestionnaire',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ feedback: feedback.trim() }),
        }
      );

      if (!response.ok) {
        throw new Error(`Request failed (${response.status})`);
      }

      setSubmitState('success');
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : 'Something went wrong. Please try again.'
      );
      setSubmitState('error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F0F4F8] to-white">
      <Navbar />

      <main className="pt-32 pb-20">
        <div className="max-w-2xl mx-auto px-6">
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-[#1E5A8E] mb-4">
            We'd Love Your Feedback
          </h1>
          <p className="text-[#6B7280] text-lg mb-10 leading-relaxed">
            Help us understand how we can improve RECOVER for everyone.
          </p>

          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            {/* Anonymity assurance banner */}
            <div className="flex items-start gap-3 bg-[#E8F1F5] border border-[#D1E3EA] rounded-xl p-4 mb-8">
              <span className="text-[#2F7FB7] text-xl mt-0.5">
                <i className="ri-shield-check-line" />
              </span>
              <div>
                <p className="text-[#1E5A8E] font-semibold text-sm mb-0.5">
                  100% Anonymous
                </p>
                <p className="text-[#4A5568] text-sm leading-relaxed">
                  Your feedback is completely anonymous. No personal information, account
                  details, or identifying data is collected or stored with your response.
                </p>
              </div>
            </div>

            {submitState === 'success' ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-[#E8F1F5] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-[#2F7FB7] text-3xl" />
                </div>
                <h2 className="text-2xl font-semibold text-[#1E5A8E] mb-2">
                  Thank you!
                </h2>
                <p className="text-[#4A5568] leading-relaxed">
                  Your feedback has been received. We truly appreciate you taking the time
                  to help us improve RECOVER.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label
                    htmlFor="cancellation-reason"
                    className="block text-[#1E5A8E] font-semibold mb-2"
                  >
                    Why did you cancel your subscription?
                  </label>
                  <p className="text-[#6B7280] text-sm mb-3">
                    Please share anything you'd like — what wasn't working for you, what
                    could be better, or what led to your decision.
                  </p>
                  <textarea
                    id="cancellation-reason"
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    rows={6}
                    placeholder="Share your thoughts here..."
                    className="w-full px-4 py-3 border border-[#D1E3EA] rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#2F7FB7] focus:border-transparent bg-white text-[#4A5568] placeholder-[#A0AEC0] resize-none transition-shadow"
                    disabled={submitState === 'submitting'}
                  />
                </div>

                {submitState === 'error' && (
                  <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm">
                    <i className="ri-error-warning-line text-base" />
                    <span>{errorMessage}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!feedback.trim() || submitState === 'submitting'}
                  className="w-full bg-[#2F7FB7] text-white font-semibold py-4 rounded-full hover:bg-[#1E5A8E] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submitState === 'submitting' ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                      Submitting...
                    </>
                  ) : (
                    'Submit Feedback'
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
